import React, { useState, useCallback, memo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import Switch from '@material-ui/core/Switch';
import { toggleMapFilterToggle } from '@actions/map-actions';
import {
  dotmapsLightGrayText,
  textColor
} from '@constants/colors';
import { listItemStyles } from '@constants/mui-theme';
import { Icon, IconButton } from '@mui';
import { isAnyMapItemLoading } from '@selectors/map-selector';
import './filter.scss';

/**
 * Base filter component for map tray filters.
 * @param {object} props - Component props.
 * @returns {JSX.Element} The filter component.
 */
const BaseTrayFilter = ({
  config,
  filterEnabled,
  // Customization props
  iconColor = dotmapsLightGrayText,
  iconStyle = 'icon',
  onToggleClick,
  renderContent,
  renderIcon: renderIconProp,
  renderLabel,
  renderToggle
}) => {
  const dispatch = useDispatch();
  const loading = useSelector(isAnyMapItemLoading);
  const [open, setOpen] = useState(false);

  const toggleOpen = useCallback(() => setOpen(prev => !prev), []);

  const handleToggleClick = useCallback(() => {
    if (onToggleClick) {
      onToggleClick();
    } else {
      dispatch(toggleMapFilterToggle(config.type));
    }
  }, [config.type, dispatch, onToggleClick]);

  const isToggleEnabled = !loading;

  const renderAction = () => (
    <IconButton
      aria-label={open ? 'collapse' : 'expand'}
      onClick={toggleOpen}
      style={listItemStyles.filterButtonStyle}
    >
      <Icon color={textColor} role="img">
        {open ? 'expand_less' : 'expand_more'}
      </Icon>
    </IconButton>
  );

  const renderDefaultToggle = () => (
    <Switch
      disabled={!isToggleEnabled}
      onClick={handleToggleClick}
      checked={filterEnabled}
      color="primary"
      size="small"
      inputProps={{ 'aria-label': config.label }}
    />
  );

  const renderDefaultIcon = () => {
    const iconName = config.icon;
    if (iconName) {
      return (
        <div styleName={iconStyle}>
          <Icon
            aria-label={config.label}
            color={iconColor}
            role="img"
            style={{ fontSize: '1.5rem' }}
          >
            {iconName}
          </Icon>
        </div>
      );
    }
    return null;
  };

  const renderIcon = renderIconProp || renderDefaultIcon;
  const label = renderLabel ? renderLabel() : config.label;
  const content = renderContent ? renderContent() : <div />;
  const hasContent = content.props.children && content.props.children.length > 0;

  if (!hasContent) {
    return (
      <div styleName="filter-container">
        <div styleName="row">
          <div styleName="toggle">
            {renderToggle ? renderToggle() : renderDefaultToggle()}
          </div>
          {renderIcon()}
          <div styleName="label">
            {label}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div styleName="filter-container">
      <div styleName="row">
        <div styleName="toggle">
          {renderToggle ? renderToggle() : renderDefaultToggle()}
        </div>
        {renderIcon()}
        <div styleName="label">
          {label}
        </div>
        <div styleName="action">
          {renderAction()}
        </div>
      </div>
      {open && (
        <div styleName="content">
          {content}
        </div>
      )}
    </div>
  );
};

BaseTrayFilter.propTypes = {
  config: PropTypes.object,
  filterEnabled: PropTypes.bool,
  iconColor: PropTypes.string,
  iconStyle: PropTypes.string,
  onToggleClick: PropTypes.func,
  renderContent: PropTypes.func,
  renderIcon: PropTypes.func,
  renderLabel: PropTypes.func,
  renderToggle: PropTypes.func
};

export default memo(BaseTrayFilter);
